import { apiClient } from './client'
import type { ApiResponse } from './client'
import type { CampaignTouchpointDto } from './leads'

// ORB-383 contact-detail API. The dashboard reaches a contact only through an OWNED anchor — a lead or
// an audience (FileImportRow) row within a campaign — never an arbitrary global ContactId. Property
// names are PascalCase to match the API's serialized DTOs.

export type ContactAnchorType = 'lead' | 'audience'

export interface ContactConsentDto {
  State: 'Subscribed' | 'Unsubscribed' | 'HardBounced'
  CanSendMarketing: boolean
}

export interface ContactFacetsDto {
  IsLead: boolean
  IsAudience: boolean
  IsVisitor: boolean
  LeadCount: number
  AudienceCount: number
  SessionCount: number
}

export interface ContactMeasuresDto {
  EmailsSent: number
  OpenRatePercent?: number | null
  Clicks: number
  Sessions: number
  Conversions: number
  LastSeenUtc?: string | null
  DealStage?: string | null
}

export interface ContactEnrichmentStateDto {
  Status?: string | null
  EnrichedAt?: string | null
  EnrichmentAvailableDate?: string | null
  AttemptCount: number
  HasUpdatedPhoneAvailable: boolean
}

export interface ContactWebSourceDto {
  WebId: string
  Source?: string | null
  HasAcceptedOffer: boolean
}

export interface ContactMembershipDto {
  Kind: 'Lead' | 'Audience' | 'Visitor'
  Label: string
  CampaignId?: string | null
}

export interface ContactDealDto {
  Stage?: string | null
  Amount?: number | null
  Currency?: string | null
  Source?: string | null
}

export interface ContactActivityPageDto {
  Path: string
  Title?: string | null
  At: string
  Type?: string | null
}

export interface ContactActivityItemDto {
  Kind: 'Interaction' | 'Web' | 'Email'
  Title: string
  Subtitle?: string | null
  OccurredAt: string
  CampaignId?: string | null
  CampaignName?: string | null
  WebId?: string | null
  EmailType?: string | null
  EmailSendId?: string | null
  DeletedInSourceAt?: string | null
  ConferenceId?: string | null
  RegistrationId?: string | null
  SessionId?: string | null
  Source?: string | null
  PageCount?: number | null
  DurationSeconds?: number | null
  Converted?: boolean | null
  // Derived on Kind==='Web' sessions (orbitalx-api#326): a non-null server-side LandingPageId
  // serializes as 'Landing page', all other sessions as 'Website' (the enum's [Description]
  // values, hence the space). Null/absent on non-Web items.
  SessionKind?: 'Website' | 'Landing page' | null
  // Derived on landing-page sessions (ORB-503): how that page is hosted, as the LandingPageMode
  // [Description] values. Null on website sessions and on API payloads cached before the field
  // existed — absence means "not known to be external", never a positive 'Builder'.
  LandingPageMode?: 'Builder' | 'External' | null
  Pages: ContactActivityPageDto[]
}

export interface ContactActivityResponseDto {
  Items: ContactActivityItemDto[]
}

export interface ContactDetailDto {
  Scope: 'Web' | 'Campaign' | 'Company'
  WorkspaceId?: string | null
  CampaignId?: string | null
  WebId?: string | null
  ClientCompanyId: string
  AnchorType: 'Lead' | 'Audience' | 'Contact'
  LeadId?: string | null
  FileImportRowId?: string | null
  ContactId?: string | null
  ETag?: string | null
  CanDelete: boolean

  FullName?: string | null
  FirstName?: string | null
  LastName?: string | null
  Email?: string | null
  JobTitle?: string | null
  CompanyName?: string | null
  CompanyIndustry?: string | null
  CompanyStaffCountRange?: string | null
  CompanyWebsite?: string | null
  CompanyDomain?: string | null
  CompanyLinkedInUrl?: string | null
  CompanyLogoUrl?: string | null
  // Editable base lead fields (hydrate the campaign-scope editor)
  CompanyLinkedInId?: string | null
  Industry?: string | null
  CompanySize?: string | null
  Country?: string | null
  Phone?: string | null
  GlobalContactPhone?: string | null
  LinkedinProfileUrl?: string | null
  ProfilePicUrl?: string | null

  Consent: ContactConsentDto
  Facets: ContactFacetsDto

  IsAudienceOnly: boolean
  SignalScore?: number | null
  SignalLevel?: string | null
  TargetingListName?: string | null
  TargetingReason?: string | null

  Measures: ContactMeasuresDto
  Enrichment: ContactEnrichmentStateDto
  WebSource?: ContactWebSourceDto | null
  Memberships: ContactMembershipDto[]
  CampaignTouchpoints: CampaignTouchpointDto[]
  Deal?: ContactDealDto | null
  RecentActivity: ContactActivityItemDto[]
}

export interface EmailEngagementSummaryDto {
  Sent: number
  Delivered: number
  Opened: number
  Clicked: number
  Bounced: number
  OpenRatePercent?: number | null
  ClickRatePercent?: number | null
}

export interface EmailEngagementItemDto {
  Id: string
  Subject?: string | null
  EmailType: string
  Status: string
  CampaignId?: string | null
  WebId?: string | null
  BlastId?: string | null
  ScheduledSendAtUtc?: string | null
  SentAtUtc?: string | null
  DeliveredAtUtc?: string | null
  FirstOpenedAtUtc?: string | null
  OpenCount: number
  FirstClickedAtUtc?: string | null
  ClickCount: number
  LastClickedUrl?: string | null
  CreatedDate: string
}

export interface EmailEngagementDto {
  Summary: EmailEngagementSummaryDto
  Items: EmailEngagementItemDto[]
}

// The anchor segment maps to the API resource collection: lead → Leads, audience → AudienceRows.
function anchorSegment(anchorType: ContactAnchorType): string {
  return anchorType === 'lead' ? 'Leads' : 'AudienceRows'
}

function basePath(workspaceId: string, campaignId: string, anchorType: ContactAnchorType, anchorId: string): string {
  const ws = encodeURIComponent(workspaceId)
  const camp = encodeURIComponent(campaignId)
  const id = encodeURIComponent(anchorId)
  return `/api/Workspaces/${ws}/Campaigns/${camp}/${anchorSegment(anchorType)}/${id}/ContactDetail`
}

export const contactDetailApi = {
  getDetail: (
    workspaceId: string,
    campaignId: string,
    anchorType: ContactAnchorType,
    anchorId: string,
    webId?: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<ContactDetailDto>> => {
    const qs = webId ? `?webId=${encodeURIComponent(webId)}` : ''
    return apiClient.get(`${basePath(workspaceId, campaignId, anchorType, anchorId)}${qs}`, { signal })
  },

  getActivity: (
    workspaceId: string,
    campaignId: string,
    anchorType: ContactAnchorType,
    anchorId: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<ContactActivityResponseDto>> =>
    apiClient.get(`${basePath(workspaceId, campaignId, anchorType, anchorId)}/Activity`, { signal }),

  getEmails: (
    workspaceId: string,
    campaignId: string,
    anchorType: ContactAnchorType,
    anchorId: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<EmailEngagementDto>> =>
    apiClient.get(`${basePath(workspaceId, campaignId, anchorType, anchorId)}/Emails`, { signal }),

  // Cross-campaign (workspace) overview. View-only.
  // A lead anchors on its representative lead id; an audience anchor is someone the workspace
  // targeted who never engaged, so has no lead to point at (ORB-623). Same DTO either way.
  getWorkspaceDetail: (
    workspaceId: string,
    anchorType: ContactAnchorType,
    anchorId: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<ContactDetailDto>> =>
    apiClient.get(
      `/api/Workspaces/${encodeURIComponent(workspaceId)}/${anchorSegment(anchorType)}/${encodeURIComponent(anchorId)}/ContactDetail`,
      { signal }
    ),
}
